import type { ReactNode } from "react";

type Row = Record<string, any>;

const pretty = (value = "") => value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
const textValue = (value: unknown) => typeof value === "string" ? value : value == null ? "Unavailable" : JSON.stringify(value);

function HookCards({ value }: { value: unknown }) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return <p>{textValue(value)}</p>;
  return <div className="analysis-hook-grid">{Object.entries(value as Record<string, any>).map(([window, hook]) => <article key={window}>
    <span>{window.replaceAll("_", "–").toUpperCase()}</span>
    {hook?.visual && <p><b>Visual</b>{textValue(hook.visual)}</p>}
    {hook?.audio && <p><b>Audio</b>{textValue(hook.audio)}</p>}
    {hook?.verdict && <strong>{textValue(hook.verdict)}</strong>}
  </article>)}</div>;
}

function KeyValue({ value }: { value: unknown }) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return <p>{textValue(value)}</p>;
  return <dl className="analysis-kv">{Object.entries(value as Record<string, any>).map(([key, item]) => <div key={key}><dt>{pretty(key)}</dt><dd>{Array.isArray(item) ? item.join(" · ") : textValue(item)}</dd></div>)}</dl>;
}

function BulletList({ items }: { items: unknown }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return <ul className="analysis-list">{items.map((item, index) => <li key={`${index}-${textValue(item).slice(0, 20)}`}>{typeof item === "string" ? item : item?.description ?? item?.beat ?? textValue(item)}</li>)}</ul>;
}

function Narrative({ items }: { items: unknown }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return <div className="analysis-narrative">{items.map((item, index) => <div key={`${index}-${item?.beat || "beat"}`}><span>{String(index + 1).padStart(2, "0")}</span><p><b>{item?.beat || `Beat ${index + 1}`}</b><small>{item?.description || textValue(item)}</small></p></div>)}</div>;
}

function Claims({ items }: { items: unknown }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return <div className="analysis-claims">{items.map((item, index) => <article key={`${index}-${item?.claim || "claim"}`}>
    <p>{item?.claim || textValue(item)}</p>
    {item?.proof_provided && <small>{item.proof_provided}</small>}
    {item?.classification && <span>{pretty(item.classification)}</span>}
  </article>)}</div>;
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return <section className="analysis-section"><h3>{label}</h3>{children}</section>;
}

export function VideoAnalysisModal({ asset, videoUrl, analyses, onClose }: { asset: Row; videoUrl: string; analyses: Row[]; onClose: () => void }) {
  const record = analyses.find((item) => item.media_asset_id === asset.id || item.label === asset.label);
  const analysis = record?.analysis ?? {};
  const title = pretty(asset.label?.replaceAll("__", " · "));

  return <div className="intel-modal" role="dialog" aria-modal="true" aria-label={`Video analysis: ${title}`} onClick={onClose}>
    <div className="intel-modal-shell" onClick={(event) => event.stopPropagation()}>
      <button className="intel-close" onClick={onClose} aria-label="Close analysis">×</button>
      <div className="intel-video-column">
        <div className="intel-video">{videoUrl ? <video src={videoUrl} controls autoPlay playsInline /> : <span className="intel-spinner" />}</div>
        <div className="video-trust-strip"><span><i /> PRIVATE MEDIA</span><p>Signed playback link expires in 30 minutes.</p></div>
      </div>
      <div className="intel-analysis">
        <header className="analysis-header"><span>VIDEO FORENSICS</span><h2>{title}</h2><div><b>{record?.transcript_language?.toUpperCase() || "MULTILINGUAL"}</b><b>{record?.sampling_fps ? `${record.sampling_fps} FPS ANALYSIS` : "FRAME VERIFIED"}</b><b>{pretty(record?.evidence_level || "model_inferred")}</b></div></header>

        <Section label="Executive summary"><p className="analysis-summary">{textValue(analysis.summary || "Forensic analysis available in the evidence record.")}</p></Section>
        <Section label="First eight seconds"><HookCards value={analysis.hook_analysis} /></Section>
        <Section label="Narrative architecture"><Narrative items={analysis.narrative_beats} /></Section>
        <Section label="Conversion mechanism"><KeyValue value={analysis.CTA} /></Section>
        <div className="analysis-two-col">
          <Section label="Visual grammar"><KeyValue value={analysis.visual_grammar} /></Section>
          <Section label="Editing & pacing"><KeyValue value={analysis.editing_and_pacing} /></Section>
        </div>
        <Section label="Claims & proof"><Claims items={analysis.claims_and_proof} /></Section>
        <div className="analysis-two-col">
          <Section label="What works"><BulletList items={analysis.strengths} /></Section>
          <Section label="Risks / friction"><BulletList items={analysis.weaknesses || analysis.likely_retention_friction} /></Section>
        </div>
        {record?.transcript && <details className="analysis-transcript"><summary>Full transcript · {record.transcript_language || "source language"}<span>OPEN</span></summary><p>{record.transcript}</p></details>}
      </div>
    </div>
  </div>;
}
